import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "./format";

const FOCUSABLE = 'a[href],button:not([disabled]),textarea,input:not([disabled]),select,[tabindex]:not([tabindex="-1"])';

let openCount = 0;

/**
 * Accessible modal: Esc and backdrop close it, focus stays inside, and the
 * page behind doesn't scroll. `footer` holds the actions (primary last).
 */
export const Modal = ({
  open, onClose, title, description, icon: Icon, tone = "gold", size = "md", footer, children,
  closeOnBackdrop = true, className, bodyClassName, initialFocusRef,
}) => {
  const panelRef = useRef(null);
  const titleId = useId();
  const lastFocus = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    openCount += 1;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      const target = initialFocusRef?.current || panelRef.current?.querySelector("[data-autofocus]") || panelRef.current?.querySelector(FOCUSABLE);
      (target || panelRef.current)?.focus?.();
    }, 20);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
      } else if (e.key === "Tab" && panelRef.current) {
        const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
        if (!nodes.length) return;
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      openCount = Math.max(0, openCount - 1);
      if (!openCount) document.body.style.overflow = "";
      lastFocus.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const width = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", full: "max-w-6xl" }[size] || "max-w-lg";
  const toneCls = {
    gold: "bg-cm-gold-soft text-cm-gold-text",
    danger: "bg-cm-danger-soft text-cm-danger",
    success: "bg-cm-success-soft text-cm-success",
    warning: "bg-cm-warning-soft text-cm-warning",
    info: "bg-cm-info-soft text-cm-info",
  }[tone];

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-cm-overlay backdrop-blur-[2px] animate-cm-fade-in"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cx(
          "relative w-full flex flex-col max-h-[92vh] bg-cm-surface text-cm-text border border-cm-border shadow-cm-lg",
          "rounded-t-cm-xl sm:rounded-cm-xl animate-cm-fade-in focus:outline-none",
          width,
          className,
        )}
      >
        {(title || Icon) && (
          <div className="flex items-start gap-3 px-5 pt-5 pb-3">
            {Icon && (
              <span className={cx("w-10 h-10 rounded-cm-lg grid place-items-center shrink-0", toneCls)}>
                <Icon size={20} />
              </span>
            )}
            <div className="flex-1 min-w-0">
              {title && <h2 id={titleId} className="font-sans text-lg font-bold m-0 leading-snug">{title}</h2>}
              {description && <p className="text-sm text-cm-muted mt-1 mb-0">{description}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 -mr-1 -mt-1 grid place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}
        <div className={cx("px-5 pb-5 overflow-y-auto", !title && !Icon && "pt-5", bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-cm-border bg-cm-surface rounded-b-cm-xl">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
